import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useCompany } from '../components/CompanySelector';
import { AccountBalanceSummary, ReportResult } from '../dto/reports.dto';
import { formatCurrency, formatDate, groupProfitLossAccounts } from '../utils/helpers';
import ReportTable from '../components/ReportTable';
import LoadingSpinner from '../components/LoadingSpinner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

const ProfitLossPage: React.FC = () => {
  const { selectedCompany } = useCompany();

  const [startDate, setStartDate] = useState<Date | null>(new Date(new Date().getFullYear(), new Date().getMonth(), 1)); // 1st of current month
  const [endDate, setEndDate] = useState<Date | null>(new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate())); // Today

  const [selectedBranches, setSelectedBranches] = useState<{ id: number; name: string }[]>([]);
  const [branches, setBranches] = useState<{ id: number; name: string }[]>([]);

  const [reportData, setReportData] = useState<AccountBalanceSummary[] | null>(null);
  const [signFlip, setSignFlip] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    axios
      .get(`${BACKEND_URL}/branches`) //Endpoint to fetch branches
      .then((res) => setBranches(res.data))
      .catch((err) => console.error('Failed to fetch branches', err));
  }, []);

  const fetchProfitLoss = async () => {
    if (!selectedCompany || !startDate || !endDate) {
      setError('Please select a company and valid date range.');
      setReportData(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      params.append('companyId', selectedCompany.id.toString());
      params.append('startDate', formatDate(startDate));
      params.append('endDate', formatDate(endDate));

      // Append branch IDs if any branches are selected
      selectedBranches.forEach(branch => {
        params.append('branchIds', branch.id.toString());
      });

      const response = await axios.get<ReportResult<AccountBalanceSummary[]>>(
        `${BACKEND_URL}/reports/profit-loss?${params.toString()}`
      );
      setReportData(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch Profit & Loss.');
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!reportData) {
      alert('No data to export!');
      return;
    }

    try {
      const response = await axios.post(
        `${BACKEND_URL}/reports/export-excel`,
        {
          reportType: 'profit-loss',
          companyId: selectedCompany?.id,
          branchIds: selectedBranches.length > 0 ? selectedBranches.map(b => b.id) : [],
          startDate: startDate ? formatDate(startDate) : undefined,
          endDate: endDate ? formatDate(endDate) : undefined,
          reportName: 'Profit & Loss',
          data: reportData, // Send the already fetched data
          includeInitialBalance: 'false',
          signFlip: signFlip,
        },
        {
          responseType: 'blob',
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `Profit_Loss_${selectedCompany?.name}_${formatDate(startDate!)}-${formatDate(endDate!)}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      alert('Failed to export report.');
    }
  };

const branchOptions = branches.map(branch => ({
    value: branch.id,
    label: branch.name,
    }));

  // const plColumns = [
  //   { header: 'Account Code', accessor: 'code' },
  //   { header: 'Account Name', accessor: 'name' },
  //   { header: 'Account Type', accessor: 'accountType' },
  //   { header: 'Internal Group', accessor: 'internalGroup' },
  //   { header: 'Debit Total', accessor: (row: AccountBalanceSummary) => formatCurrency(row.debitTotal), className: 'text-right' },
  //   { header: 'Credit Total', accessor: (row: AccountBalanceSummary) => formatCurrency(row.creditTotal), className: 'text-right' },
  //   { header: 'Balance', accessor: (row: AccountBalanceSummary) => formatCurrency(row.balance), className: 'font-semibold text-right' },
  // ];
  
const plColumns = [
  { header: 'Account', accessor: 'name' },
  { header: 'Code', accessor: 'code' },
  {
    header: 'Balance',
    accessor: (row: AccountBalanceSummary) => formatCurrency(row.balance), // Changed to a function
    headerClassName: 'text-right',
    className: 'text-right font-semibold',
  },
];

  const groupedData = reportData ? groupProfitLossAccounts(reportData, signFlip) : null;

  return (
    <div className="p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-3xl font-bold mb-6 text-gray-800">Profit & Loss Report</h2>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 items-end">
        <div>
          <label htmlFor="startDate" className="block text-sm font-medium text-gray-700">Start Date:</label>
          <DatePicker
            selected={startDate}
            onChange={(date: Date | null) => setStartDate(date)}
            dateFormat="yyyy-MM-dd"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2"
          />
        </div>
        <div>
          <label htmlFor="endDate" className="block text-sm font-medium text-gray-700">End Date:</label>
          <DatePicker
            selected={endDate}
            onChange={(date: Date | null) => setEndDate(date)}
            dateFormat="yyyy-MM-dd"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2"
          />
        </div>

        <div className="col-span-1 md:col-span-2">
          <button
            onClick={fetchProfitLoss}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            disabled={loading || !selectedCompany}
          >
            {loading ? 'Generating...' : 'Generate Report'}
          </button>
        </div>

        {/* Branch Multi-Select */}
        <div>
          <label htmlFor="branchSelector" className="block text-sm font-medium text-gray-700">Branch:</label>
          <Select
            id="branchSelector"
            options={branchOptions}
            isMulti
            placeholder="All Branches"
            value={branchOptions.filter(option => selectedBranches.some(b => b.id === option.value))}
            onChange={(selectedOptions) => {
              setSelectedBranches(selectedOptions.map((option) => ({ id: option.value, name: option.label })));
            }}
            className="mt-1"
          />
        </div>

        <div className="col-span-1 md:col-span-2 flex items-left">
          <input
            type="checkbox"
            checked={signFlip}
            onChange={() => setSignFlip((prev) => !prev)}
            className="mr-2 mt-3"
          />
          <label className="mr-2 mt-3 text-sm font-medium text-gray-700">Sign Flipping</label>
        </div>

      </div>

      {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">{error}</div>}

      {loading ? (
        <LoadingSpinner />
      ) : reportData && reportData.length > 0 ? (
        <>
          <div className="mb-4">
            <button
              onClick={handleExport}
              className="bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
            >
              Export to XLSX
            </button>
          </div>

          {groupedData && (
            <div className="space-y-8">
              {/* Income */}
              <div>
                <h3 className="text-xl font-bold text-gray-700 mb-3 border-b pb-2 border-gray-300">Income</h3>
                  <div className="flex items-center ml-3 mb-2">
                    <span className="text-base font-normal text-gray-600">Operating Income</span>
                    <span className="ml-auto text-base font-normal text-gray-600 text-right mr-6">
                      {formatCurrency(groupedData.totalOperatingIncome)}
                    </span>
                  </div>
                  <ReportTable columns={plColumns} data={groupedData.income} columnWidths={['50%', '16.66%', '33.33%']}/>
                  <div className="flex items-center ml-3 mt-3 mb-2">
                    <span className="text-base font-normal text-gray-600">Other Income</span>
                    <span className="ml-auto text-base font-normal text-gray-600 text-right mr-6">
                      {formatCurrency(groupedData.totalOtherIncome)}
                    </span>
                  </div>
                  <ReportTable columns={plColumns} data={groupedData.otherIncome} columnWidths={['50%', '16.66%', '33.33%']}/>
                <div className="mt-4 p-3 bg-blue-50 rounded-md shadow-inner text-right font-bold text-lg">
                  Total Income: {formatCurrency(groupedData.totalIncome)}
                </div>
                  <div className="flex items-center ml-3 mt-3 mb-2">
                    <span className="text-base font-normal text-gray-600">Cost of Revenue</span>
                    <span className="ml-auto text-base font-normal text-gray-600 text-right mr-6">
                      {formatCurrency(groupedData.totalCostOfRevenue)}
                    </span>
                  </div>
                  <ReportTable columns={plColumns} data={groupedData.costOfRevenue} columnWidths={['50%', '16.66%', '33.33%']}/>
                <div className="mt-4 p-3 bg-blue-50 rounded-md shadow-inner text-right font-bold text-lg">
                  Total Gross Profit: {formatCurrency(groupedData.totalGrossProfit)}
                </div>
              </div>

              {/* Expenses */}
              <div>
                <h3 className="text-xl font-bold text-gray-700 mb-3 border-b pb-2 border-gray-300">Expenses</h3>
                <ReportTable columns={plColumns} data={groupedData.expenses} columnWidths={['50%', '16.66%', '33.33%']}/>
                <div className="mt-4 p-3 bg-blue-50 rounded-md shadow-inner text-right font-bold text-lg">
                  Total Expenses: {formatCurrency(groupedData.totalOperatingExpenses)}
                </div>
              </div>

              {/* Net Profit/Loss */}
              <div className="mt-8 p-6 bg-blue-100 rounded-lg shadow-lg text-center font-extrabold text-2xl">
                Net Profit/Loss: {formatCurrency(groupedData.netProfitLoss)}
              </div>
            </div>
          )}
        </>
      ) : (
        <p className="text-gray-500 text-center">No data available for the selected criteria. Please generate the report.</p>
      )}
    </div>
  );
};

export default ProfitLossPage;